import {
  AccountingReportType,
  ClientType,
  ENDPOINTS,
  Status,
  WebTable
} from '@constants';
import { Prototype } from '@core';
import { useResolverForm } from '@hooks';
import {
  useExportOperationReportData,
  useMutateFetchOperationReportData
} from '@hooks/report/operation';
import { IReportAccountingFilter } from '@pages/ReportV2/helpers';
import { AgUtils } from '@utils';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import DateTimeInput from 'components/CustomInput/DateTime';
import { map } from 'lodash';
import { memo, useCallback, useMemo } from 'react';
import { Controller, FormProvider } from 'react-hook-form';
import trans from 'translation';
import { KChip, KContainer, KForm, KGrid, KInput, KLabel } from 'uikit';
import * as yup from 'yup';

import Item from '../../_components/Item';

interface IFormData
  extends Omit<
    IReportAccountingFilter,
    'consigns' | 'vessels' | 'dateType' | 'jobStatus'
  > {}

const schema = yup.object().shape({
  fromDate: yup
    .mixed()
    .nullable()
    .test(
      'nullBoth',
      trans('validation.both_fields_are_either_empty_or_not_empty'),
      function (v: any) {
        const { toDate } = this.parent;
        return !!v == !!toDate;
      }
    )
    .test(
      'beforeToDate',
      trans('validation.max_date', { date: 'To Date field' }),
      function (v: any) {
        const { toDate } = this.parent;
        return (
          Prototype.date.toMoment(v)?.isSameOrBefore(toDate) || (!v && !toDate)
        );
      }
    )
    .test(
      'maxRange',
      trans('validation.max_date_range_is_six_months'),
      function (v: any) {
        const { toDate } = this.parent;
        if (!v && !toDate) {
          return true;
        }
        const range = Prototype.date.toMoment(v)?.clone().add(6, 'months');
        const toMoment = Prototype.date.toMoment(toDate)?.clone();

        return toMoment?.isSameOrBefore(range);
      }
    ),
  toDate: yup
    .mixed()
    .nullable()
    .test(
      'nullBoth',
      trans('validation.both_fields_are_either_empty_or_not_empty'),
      function (v: any) {
        const { fromDate } = this.parent;
        return !!v == !!fromDate;
      }
    )
    .test(
      'beforeToDate',
      trans('validation.min_date', { date: 'From Date field' }),
      function (v: any) {
        const { fromDate } = this.parent;
        return (
          Prototype.date.toMoment(v)?.isSameOrAfter(fromDate) ||
          (!v && !fromDate)
        );
      }
    )
    .test(
      'maxRange',
      trans('validation.max_date_range_is_six_months'),
      function (v: any) {
        const { fromDate } = this.parent;
        if (!v && !fromDate) {
          return true;
        }
        const range = Prototype.date
          .toMoment(fromDate)
          ?.clone()
          .add(6, 'months');
        const toMoment = Prototype.date.toMoment(v)?.clone();
        return toMoment?.isSameOrBefore(range);
      }
    )
});

const ReportPayment = () => {
  const colDefs = useMemo<ColDef<any>[]>(
    () => [
      {
        headerName: trans('payment'),
        field: 'paymentNumber',
        pinned: true,
        minWidth: 200
      },
      {
        headerName: trans('payment_date'),
        field: 'paymentDate',
        pinned: true,
        cellClass: 'ag-cell-full-width',
        cellRenderer: ({ value = null, node }: ICellRendererParams) => {
          return (
            <KContainer.View
              dp="flex"
              row
              justifyContent={node.rowPinned ? 'flex-end' : 'flex-start'}
            >
              <KLabel.Text
                typo={node.rowPinned ? 'TextNmBold' : 'TextNmNormal'}
              >
                {value}
              </KLabel.Text>
            </KContainer.View>
          );
        }
      },
      {
        headerName: trans('ar_client_code'),
        field: 'arClientCode'
      },
      {
        headerName: trans('ar_client_name'),
        field: 'arClientName'
      },
      {
        headerName: trans('sync_status'),
        field: 'syncStatus'
      },
      {
        headerName: trans('last_sync_date'),
        field: 'lastSyncDate'
      },
      {
        headerName: trans('last_sync_by'),
        field: 'lastSyncBy'
      },
      {
        headerName: trans('ref_no'),
        field: 'referenceNumber'
      },
      {
        headerName: trans('total_amount'),
        field: 'totalAmount',
        ...AgUtils.options.currency()
      },
      {
        headerName: trans('payment_status'),
        field: 'paymentStatus'
      },
      {
        headerName: trans('note'),
        field: 'note'
      },
      {
        headerName: trans('payment_method'),
        field: 'paymentMethod'
      },
      {
        headerName: trans('account_number'),
        field: 'accountNumber'
      },
      {
        headerName: trans('account_name'),
        field: 'accountName'
      },
      {
        headerName: trans('bank_name'),
        field: 'bankName'
      },
      {
        headerName: trans('bank_branch'),
        field: 'bankBranch'
      }
    ],
    []
  );

  const {
    data,
    mutate: mutateGenerate,
    isLoading
  } = useMutateFetchOperationReportData();

  const { mutate: mutateExport, isLoading: isExporting } =
    useExportOperationReportData();

  const methods = useResolverForm<IFormData>({
    schema,
    configs: {
      values: {
        isExport: false,
        ars: [],
        fromDate: '',
        toDate: ''
      }
    }
  });

  const onFormValid = useCallback(
    (_data: IFormData) => {
      const {
        isExport,
        ars,
        fromDate: _fromDate,
        toDate: _toDate,
        ...rest
      } = _data;

      const arIds = map(ars, 'id');

      const _params = {
        reportExcelType: AccountingReportType.PAYMENT.split('_')[0],
        arIds,
        dateType: _fromDate || _toDate ? 'PAYMENT_DATE' : undefined,
        fromDate: Prototype.date.formatDB(_fromDate),
        toDate: Prototype.date.formatDB(_toDate),
        ...rest
      };

      const mutate = isExport ? mutateExport : mutateGenerate;

      mutate(_params);
    },
    [mutateExport, mutateGenerate]
  );

  const renderPinnedBottomRowDataFunc = useCallback((_data: any) => {
    return [
      {
        paymentNumber: trans('total_payments'),
        paymentDate: _data?.totalPayments ?? 0
      },
      {
        paymentNumber: trans('total_paid_amount'),
        paymentDate: Prototype.number.formatCurrency(
          _data?.totalPaidAmount ?? 0
        )
      },
      {
        paymentNumber: trans('average_amount_per_payment'),
        paymentDate: Prototype.number.formatCurrency(
          _data?.averageAmountPerPayment ?? 0
        )
      },
      {
        paymentNumber: trans('synced_payments'),
        paymentDate: _data?.syncedPayments ?? 0
      },
      {
        paymentNumber: trans('unsynced_payments'),
        paymentDate: _data?.unsyncedPayments ?? 0
      },
      {
        paymentNumber: trans('failed_sync_payments'),
        paymentDate: _data?.failedSyncPayments ?? 0
      },
      {
        paymentNumber: trans('direct_debit_payments'),
        paymentDate: _data?.directDebitPayments ?? 0
      },
      {
        paymentNumber: trans('cash_payments'),
        paymentDate: _data?.cashPayments ?? 0
      },
      {
        paymentNumber: trans('eftpos_payments'),
        paymentDate: _data?.eftposPayments ?? 0
      },
      {
        paymentNumber: trans('direct_debit_payments_amount'),
        paymentDate: Prototype.number.formatCurrency(
          _data?.directDebitPaymentsAmount ?? 0
        )
      },
      {
        paymentNumber: trans('cash_payments_amount'),
        paymentDate: Prototype.number.formatCurrency(
          _data?.cashPaymentsAmount ?? 0
        )
      },
      {
        paymentNumber: trans('eftpos_payments_amount'),
        paymentDate: Prototype.number.formatCurrency(
          _data?.eftposPaymentsAmount ?? 0
        )
      }
    ];
  }, []);
  return (
    <FormProvider {...methods}>
      <KForm onSubmit={methods.handleSubmit(onFormValid)}>
        <KGrid.Container>
          <KGrid.Item xs={6}>
            <Controller
              name="ars"
              control={methods.control}
              render={({ field }) => {
                return (
                  <KInput.Autocomplete
                    {...field}
                    label={trans('ar_client')}
                    multiple
                    renderTags={(tagValue, getTagProps) => {
                      return tagValue?.map((i: any, index) => {
                        const isActive = i?.status === Status.Active;
                        return (
                          <KChip
                            key={i.id}
                            {...getTagProps({ index })}
                            label={i.name}
                            isLink
                            onPress={() => {
                              window.open(`/admin/clients/${i.id}`);
                            }}
                            color={isActive ? undefined : 'secondary'}
                          />
                        );
                      });
                    }}
                    apiURL={ENDPOINTS.client()}
                    apiParams={{
                      typeIn: [ClientType.AccountReceivable],
                      status: Status.Active
                    }}
                  />
                );
              }}
            />
          </KGrid.Item>
          <KGrid.Item xs={3}>
            <Controller
              name="fromDate"
              control={methods.control}
              render={({ field, fieldState: { error } }) => (
                <DateTimeInput
                  {...field}
                  kind="date"
                  label={trans('from_date')}
                  message={error?.message}
                  // disabled={!dateType}
                />
              )}
            />
          </KGrid.Item>
          <KGrid.Item xs={3}>
            <Controller
              name="toDate"
              control={methods.control}
              render={({ field, fieldState: { error } }) => (
                <DateTimeInput
                  {...field}
                  kind="date"
                  label={trans('to_date')}
                  message={error?.message}
                />
              )}
            />
          </KGrid.Item>
          <KGrid.Item xs={12}>
            <Item
              name={WebTable.ACCOUNTING_REPORT_STATEMENT}
              colDefs={colDefs}
              data={data}
              onSubmit={onFormValid}
              isGenerating={isLoading}
              isExporting={isExporting}
              renderPinnedBottomRowDataFunc={renderPinnedBottomRowDataFunc}
              height={1000}
            />
          </KGrid.Item>
        </KGrid.Container>
      </KForm>
    </FormProvider>
  );
};

export default memo(ReportPayment);
